import { components } from "@/api-schema";
import { PathsV1ApplicationSearchPostParametersQueryExtend } from "@/api-schema-enums";
import { mapAttributeValueToName } from "@/common/helpers/map-attribute-value-to-name";
import { cn } from "@/common/helpers/utils";
import { ApplicationTable } from "@/components/data-table/predefined/application-table";
import { applicationTableDefaultColumns } from "@/components/data-table/predefined/application-table-columns";
import { DataTableHeightMode, DataTableRow, ImplementedDisplayColumnDef } from "@/components/data-table/types";
import { SearchInput } from "@/components/search-input/search-input";
import { Badge, BadgeColorVariant, BadgeVariant } from "@/components/ui/badge";
import { Button, ButtonColorVariant, ButtonSize, ButtonVariant } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { FormControl, FormField, FormItem, FormLabel } from "@/components/ui/form";
import { ChevronRight } from "lucide-react";
import type { ReactElement } from "react";
import { useCallback, useMemo, useRef, useState } from "react";
import { UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useLocation } from "wouter";
import { z } from "zod";
import { createGroupDetailPath, GroupDetailTab } from "../../_helpers/group-detail-path";

export const formSchema = z.object({
  appRoles: z.array(z.string()),
});

export type formSchemaType = z.infer<typeof formSchema>;

type Application = components["schemas"]["Application"];
type AppRole = components["schemas"]["AppRole"];
type AppRoleAttribute = components["schemas"]["AppRoleAttribute"];

export interface GroupAppRoleFormFieldsType {
  data: Application[];
  isLoading: boolean;
  form: UseFormReturn<formSchemaType>;
  groupUuid?: string;
  attributesByUuid: Map<string, AppRoleAttribute>;
  onSearchChange: (value: string) => void;
  page: number;
  pageSize: number;
  totalItems?: number;
  onPaginationChange: (pagination: { page: number; pageSize: number }) => void;
}
export function GroupAppRoleFormFields({
  data,
  isLoading,
  form,
  groupUuid,
  attributesByUuid,
  onSearchChange,
  page,
  pageSize,
  totalItems,
  onPaginationChange,
}: GroupAppRoleFormFieldsType): ReactElement {
  const { t } = useTranslation(["ram", "common", "masterdata"]);
  const [_, navigate] = useLocation();
  const [selected] = form.watch(["appRoles"]);
  const subSearchValuesRef = useRef<Record<string, string>>({});
  const [expandedGroupsByApplication, setExpandedGroupsByApplication] = useState<Record<string, string[]>>({});

  const sortedAppRolesByApplication = useMemo((): Map<string, AppRole[]> => {
    const result = new Map<string, AppRole[]>();
    for (const application of data) {
      const appRoles = application.appRoles ? [...application.appRoles] : [];
      appRoles.sort((left, right) => left.name.localeCompare(right.name));
      result.set(application.uuid, appRoles);
    }
    return result;
  }, [data]);

  const customColumns = useMemo(
    () => [
      applicationTableDefaultColumns.columnImage,
      applicationTableDefaultColumns.columnName,
      {
        id: "selectedAppRoles",
        size: 300,
        header: t("ram:Selected"),
        enableSorting: false,
        cell: ({ row }: { row: DataTableRow<components["schemas"]["Application"]> }) => {
          const application = row.original;
          const appRoles = application.appRoles || [];
          const selectedAppRoles = appRoles.filter((appRole) => selected.includes(appRole.uuid));
          const attributeCount = countAttributesForApplication(attributesByUuid, application.uuid);
          const totalCount = appRoles.length + attributeCount;

          if (totalCount === 0) {
            return <span className="text-muted-foreground text-sm">{t("ram:NoAppRoles")}</span>;
          }

          const selectedCount = selectedAppRoles.length + attributeCount;

          return (
            <span className="text-sm font-medium">
              {selectedCount} / {totalCount}
            </span>
          );
        },
      } as ImplementedDisplayColumnDef<components["schemas"]["Application"]>,
    ],
    [selected, attributesByUuid, t],
  );

  const appRoleSubComponent = useCallback(
    ({ row }: { row: DataTableRow<components["schemas"]["Application"]> }): ReactElement => {
      const application = row.original;
      const initialSearchValue = subSearchValuesRef.current[application.uuid] ?? "";
      const expandedGroupNames = expandedGroupsByApplication[application.uuid] ?? [];
      const appRoles = sortedAppRolesByApplication.get(application.uuid) ?? [];
      const attributes = getApplicationAttributes(attributesByUuid, application.uuid);

      if ((!appRoles || appRoles.length === 0) && attributes.length === 0) {
        return <div className="text-muted-foreground px-4 py-2 text-sm">{t("ram:NoAppRolesAvailable")}</div>;
      }

      const hasRegularRoles = appRoles.length > 0;
      const hasAttributes = attributes.length > 0;

      return (
        <div className="p-2">
          {hasRegularRoles && (
            <AppRoleListWithFilter
              appRoles={appRoles}
              form={form}
              selected={selected}
              initialSearchValue={initialSearchValue}
              expandedGroupNames={expandedGroupNames}
              onSearchPersist={(value: string): void => {
                subSearchValuesRef.current[application.uuid] = value;
              }}
              onExpandedGroupsChange={(groupNames: string[]): void => {
                setExpandedGroupsByApplication((previousGroups) => {
                  const currentGroups = previousGroups[application.uuid] ?? [];
                  if (areStringArraysEqual(currentGroups, groupNames)) {
                    return previousGroups;
                  }

                  return {
                    ...previousGroups,
                    [application.uuid]: groupNames,
                  };
                });
              }}
            />
          )}

          {hasAttributes && (
            <div>
              <h4 className="mb-3 text-sm font-semibold">
                {t("ram:AttributeAppRolesCount", { count: attributes.length })}
              </h4>
              <div className="ml-6">
                <div className="flex flex-wrap gap-2">
                  {attributes.map((attribute) => (
                    <Badge
                      key={attribute.uuid}
                      variant={BadgeVariant.Outlined}
                      colorVariant={BadgeColorVariant.Secondary}
                      tooltip={t("ram:AttributeAppRoleToolTip")}
                    >
                      {mapAttributeValueToName(attribute.type) || attribute.type}
                    </Badge>
                  ))}
                </div>
                <p className="text-muted-foreground mt-2 text-xs">
                  {t("ram:AttributesCanOnlyBeManagedAtApplicationLevel")}
                </p>
              </div>
            </div>
          )}
        </div>
      );
    },
    [attributesByUuid, expandedGroupsByApplication, form, selected, sortedAppRolesByApplication, t],
  );

  const getRowCanExpand = useCallback(
    (application: components["schemas"]["Application"]): boolean => {
      const hasAppRoles = (application.appRoles?.length ?? 0) > 0;
      const hasAttributes = countAttributesForApplication(attributesByUuid, application.uuid) > 0;
      return hasAppRoles || hasAttributes;
    },
    [attributesByUuid],
  );

  return (
    <>
      <FormField
        control={form.control}
        name="appRoles"
        render={() => (
          <FormItem className="flex min-h-0 flex-col">
            <FormLabel>{t("ram:AppRoles")}</FormLabel>
            <FormControl>
              <ApplicationTable
                autoFetch={false}
                showCreateButton={false}
                onView={(application) => navigate(`~/ram/application/${application.uuid}`)}
                onEdit={(application) => navigate(`~/ram/application/${application.uuid}/update`)}
                columns={customColumns}
                searchPlaceholder={t("ram:SearchByAppOrAppRole")}
                onSearchChange={onSearchChange}
                query={{ extend: [PathsV1ApplicationSearchPostParametersQueryExtend.AppRoles] }}
                data={data}
                isLoading={isLoading}
                page={page}
                pageSize={pageSize}
                totalItems={totalItems}
                serverSidePagination
                onPaginationChange={onPaginationChange}
                features={{
                  expansion: true,
                  multiSelection: false,
                  pagination: true,
                  search: true,
                }}
                heightMode={DataTableHeightMode.Content}
                subComponent={appRoleSubComponent}
                getRowCanExpand={getRowCanExpand}
              />
            </FormControl>
          </FormItem>
        )}
      />

      <div className="flex shrink-0 flex-wrap justify-end gap-4">
        <Button
          disabled={isLoading}
          onClick={() =>
            navigate(groupUuid ? createGroupDetailPath(groupUuid, GroupDetailTab.AppRoles) : "~/ram/group")
          }
          type="button"
          variant={ButtonVariant.Outlined}
          colorVariant={ButtonColorVariant.Secondary}
        >
          {t("common:Cancel")}
        </Button>
        <Button disabled={isLoading} type="submit">
          {t("common:Save")}
        </Button>
      </div>
    </>
  );
}

function belongsToApplication(attribute: AppRoleAttribute, applicationUuid: string): boolean {
  return (
    attribute.application !== null &&
    typeof attribute.application === "object" &&
    attribute.application.uuid === applicationUuid
  );
}

function countAttributesForApplication(
  attributesByUuid: Map<string, AppRoleAttribute>,
  applicationUuid: string,
): number {
  return Array.from(attributesByUuid.values()).filter((a) => belongsToApplication(a, applicationUuid)).length;
}

function getApplicationAttributes(
  attributesByUuid: Map<string, AppRoleAttribute>,
  applicationUuid: string,
): AppRoleAttribute[] {
  return Array.from(attributesByUuid.values()).filter((a) => belongsToApplication(a, applicationUuid));
}

function collectAppRoleUuids(appRoles: AppRole[]): string[] {
  return appRoles.map((appRole) => appRole.uuid);
}

function areAllUuidsSelected(uuids: string[], selected: string[]): boolean {
  if (uuids.length === 0) return false;
  const selectedSet = new Set(selected);
  for (const uuid of uuids) {
    if (!selectedSet.has(uuid)) return false;
  }
  return true;
}

function addUuidsToSelection(selected: string[], uuids: string[]): string[] {
  return Array.from(new Set([...selected, ...uuids]));
}

function removeUuidsFromSelection(selected: string[], uuids: string[]): string[] {
  const remove = new Set(uuids);
  return selected.filter((uuid) => !remove.has(uuid));
}

type AppRoleListEntry = { kind: "group"; name: string; items: AppRole[] } | { kind: "item"; item: AppRole };

export function groupAppRolesByPrefix(appRoles: AppRole[]): AppRoleListEntry[] {
  const groups = new Map<string, AppRole[]>();
  const singles: AppRole[] = [];

  for (const role of appRoles) {
    const dotIndex = role.name.indexOf(".");
    if (dotIndex > 0) {
      const prefix = role.name.slice(0, dotIndex);
      const current = groups.get(prefix) ?? [];
      current.push(role);
      groups.set(prefix, current);
    } else {
      singles.push(role);
    }
  }

  const groupedEntries: AppRoleListEntry[] = Array.from(groups.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([name, items]) => ({
      kind: "group" as const,
      name,
      items: [...items].sort((a, b) => a.name.localeCompare(b.name)),
    }));

  const singleEntries: AppRoleListEntry[] = [...singles]
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((item) => ({ kind: "item" as const, item }));

  return [...groupedEntries, ...singleEntries];
}

interface AppRoleListWithFilterProps {
  appRoles: AppRole[];
  form: UseFormReturn<formSchemaType>;
  selected: string[];
  initialSearchValue: string;
  expandedGroupNames: string[];
  onSearchPersist: (value: string) => void;
  onExpandedGroupsChange: (groupNames: string[]) => void;
}

function AppRoleListWithFilter({
  appRoles,
  form,
  selected,
  initialSearchValue,
  expandedGroupNames,
  onSearchPersist,
  onExpandedGroupsChange,
}: AppRoleListWithFilterProps): ReactElement {
  const { t } = useTranslation(["common", "ram", "masterdata"]);
  const [searchValue, setSearchValue] = useState<string>(initialSearchValue);
  const filteredAppRoles = useMemo(
    (): AppRole[] => filterAppRolesBySearch(appRoles, searchValue),
    [appRoles, searchValue],
  );
  const groupedEntries = useMemo((): AppRoleListEntry[] => groupAppRolesByPrefix(filteredAppRoles), [filteredAppRoles]);
  const appRoleUuids = useMemo((): string[] => collectAppRoleUuids(filteredAppRoles), [filteredAppRoles]);
  const isAllSelected = useMemo((): boolean => areAllUuidsSelected(appRoleUuids, selected), [appRoleUuids, selected]);
  const expandedGroups = useMemo((): Set<string> => new Set(expandedGroupNames), [expandedGroupNames]);

  const toggleAppRole = useCallback(
    (uuid: string): void => {
      form.setValue(
        "appRoles",
        selected.includes(uuid) ? selected.filter((item) => item !== uuid) : [...selected, uuid],
      );
    },
    [selected, form],
  );

  const handleToggleAll = useCallback((): void => {
    if (appRoleUuids.length === 0) return;
    form.setValue(
      "appRoles",
      isAllSelected ? removeUuidsFromSelection(selected, appRoleUuids) : addUuidsToSelection(selected, appRoleUuids),
    );
  }, [appRoleUuids, isAllSelected, selected, form]);

  const handleSearchChange = useCallback(
    (value: string): void => {
      setSearchValue(value);
      onSearchPersist(value);
    },
    [onSearchPersist],
  );

  const toggleGroup = useCallback(
    (groupName: string): void => {
      const next = new Set(expandedGroupNames);
      if (next.has(groupName)) next.delete(groupName);
      else next.add(groupName);
      onExpandedGroupsChange(Array.from(next).sort((left, right) => left.localeCompare(right)));
    },
    [expandedGroupNames, onExpandedGroupsChange],
  );

  return (
    <div className="mb-4">
      <div className="mb-3 flex items-center justify-between">
        <h4 className="text-sm font-semibold">
          {t("ram:AppRoles")} ({filteredAppRoles.length})
        </h4>
        <Button
          type="button"
          variant={ButtonVariant.Ghost}
          colorVariant={ButtonColorVariant.Secondary}
          size={ButtonSize.Sm}
          onClick={handleToggleAll}
          disabled={appRoleUuids.length === 0}
        >
          {isAllSelected ? t("common:ClearAll") : t("common:SelectAll")}
        </Button>
      </div>

      <div className="mb-3">
        <SearchInput placeholder={t("ram:SearchByAppOrAppRole")} value={searchValue} onChange={handleSearchChange} />
      </div>

      <div className="grid gap-2">
        {groupedEntries.map((entry, index) => {
          const showTopBorder = index !== 0;

          if (entry.kind === "group") {
            const expanded = expandedGroups.has(entry.name);
            const contentId = `app-role-group-${entry.name.replace(/\s+/g, "-").toLowerCase()}`;

            return (
              <Collapsible
                key={entry.name}
                open={expanded}
                onOpenChange={() => toggleGroup(entry.name)}
                className={cn(showTopBorder && "border-t")}
              >
                <CollapsibleTrigger asChild>
                  <button
                    type="button"
                    className="hover:bg-muted/50 flex w-full cursor-pointer items-center px-4 py-4 text-left transition-colors"
                    aria-controls={contentId}
                    aria-label={
                      expanded
                        ? t("ram:CollapseAppRoleGroupAriaLabel", { name: entry.name })
                        : t("ram:ExpandAppRoleGroupAriaLabel", { name: entry.name })
                    }
                  >
                    <span className="mr-3">
                      <ChevronRight
                        className={cn("h-4 w-4 transition-transform duration-200", expanded && "rotate-90")}
                      />
                    </span>
                    <span className="text-sm font-medium">
                      {entry.name}
                      <span className="text-muted-foreground ml-1">({entry.items.length})</span>
                    </span>
                  </button>
                </CollapsibleTrigger>

                <CollapsibleContent id={contentId}>
                  <div className="grid gap-2">
                    {entry.items.map((appRole, idx) => (
                      <div
                        key={appRole.uuid}
                        className={cn("flex w-full items-center py-4 pr-4 pl-12", idx !== 0 && "border-t")}
                      >
                        <Checkbox
                          value={appRole.uuid}
                          checked={selected.includes(appRole.uuid)}
                          onCheckedChange={() => toggleAppRole(appRole.uuid)}
                        />
                        <div className="ml-4">
                          {appRole.name}
                          {appRole.v1Id && (
                            <span className="text-muted-foreground ml-2 text-xs">
                              ({t("masterdata:V1ID")}: {appRole.v1Id})
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </CollapsibleContent>
              </Collapsible>
            );
          }

          const appRole = entry.item;
          return (
            <div key={appRole.uuid} className={cn("flex w-full items-center px-4 py-4", showTopBorder && "border-t")}>
              <Checkbox
                value={appRole.uuid}
                checked={selected.includes(appRole.uuid)}
                onCheckedChange={() => toggleAppRole(appRole.uuid)}
              />
              <div className="ml-4">
                {appRole.name}
                {appRole.v1Id && (
                  <span className="text-muted-foreground ml-2 text-xs">
                    ({t("masterdata:V1ID")}: {appRole.v1Id})
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function filterAppRolesBySearch(appRoles: AppRole[], searchValue: string): AppRole[] {
  const freeText = normalizeSearchValue(searchValue);
  if (!freeText) return appRoles;
  return appRoles.filter((appRole) => matchesAppRoleFreeText(appRole, freeText));
}

function normalizeSearchValue(value: string): string {
  return value.trim().toLowerCase();
}

function matchesAppRoleFreeText(appRole: AppRole, query: string): boolean {
  if (matchesText(appRole.name, query)) return true;
  if (appRole.v1Id !== undefined && String(appRole.v1Id).includes(query)) return true;
  return false;
}

function matchesText(value: string | undefined, query: string): boolean {
  if (!query) return true;
  if (!value) return false;
  return value.toLowerCase().includes(query);
}

function areStringArraysEqual(left: string[], right: string[]): boolean {
  if (left.length !== right.length) return false;

  for (let index = 0; index < left.length; index++) {
    if (left[index] !== right[index]) return false;
  }

  return true;
}
